//* Third Party Libraries
import classNames from "classnames";
import {
  Accordion,
  AccordionItem,
  AccordionItemTitle,
  AccordionItemBody
} from "react-accessible-accordion";

//* Local or Custom Libraries
import { Icon, RadioButton } from "@lululemon/ui-library";
import { caratIcon } from "@lululemon/ui-icons";
import {
  INVENTORY_TYPE,
  MAX_QUANTITY_TO_DISPLAY
} from "../utilities/constants";

//* Importing Styles
import styles from "../bopis-modal.module.scss";

/**
 * @param {object|undefined} activeStoreRadioBtn - an object containing id and name of the current selected store.
 * @param {string|undefined} inventoryType - a string representing the type of inventory
 * @param {array} nearbyBopisStores - an array that contains all neaby stores
 * @param {function|undefined} setActiveStoreRadioBtn - the setter for the activeStoreRadioBtn state in the connector
 * @param {object|undefined} selectedStore - an object that contains id and name of selected store
 * @param {string} unitOfDistance - a string representing the unit of distance used KM or miles.
 */
const BopisModalStoreList = ({
  activeStoreRadioBtn,
  inventoryType,
  nearbyBopisStores,
  setActiveStoreRadioBtn,
  selectedStore,
  unitOfDistance
}) => {
  const getChecked = (id) => {
    const activeStoreId = activeStoreRadioBtn?.id;
    const selectedStoreId = selectedStore?.id;
    if (!activeStoreId) return selectedStoreId === id;
    return activeStoreId === id;
  };

  const displayAvailableItems = (bopisQuantity) => {
    const quantity = Math.min(bopisQuantity, MAX_QUANTITY_TO_DISPLAY);
    return `${quantity} ${bopisQuantity > 1 ? "items" : "item"} available`;
  };

  return (
    <Accordion>
      {nearbyBopisStores.map(
        (
          {
            id,
            name,
            address1,
            address2,
            city,
            state,
            postalcode,
            country,
            phone,
            formattedPhone,
            hours,
            unformattedHours,
            specialHours,
            latitude,
            longitude,
            distanceToStore,
            bopisQuantity
          },
          index
        ) => {
          return (
            <AccordionItem key={id}>
              <AccordionItemTitle
                aria-label={`accordion item for store ${name}`} // prevents bug where title is read twice by screenreader
                className={classNames({
                  [styles.accordionItemTitle]:
                    inventoryType == INVENTORY_TYPE.BOPIS,
                  [styles.accordionItemTitleNoBopisInventory]:
                    inventoryType != INVENTORY_TYPE.BOPIS
                })}
                hideBodyClassName={styles.accordionItemTitleCollapsed}
              >
                <div>
                  {inventoryType === INVENTORY_TYPE.BOPIS ? (
                    <>
                      <RadioButton
                        id={`modal-radio-${index + 1}`}
                        name="modal-store-list-radio-group"
                        classes={{
                          label: `${styles.radioButtonLabel}`
                        }}
                        onChange={() =>
                          setActiveStoreRadioBtn({
                            id,
                            name,
                            bopisQuantity,
                            distanceToStore,
                            unformattedHours,
                            specialHours,
                            address: {
                              address1,
                              address2,
                              city,
                              state,
                              postalcode,
                              country
                            }
                          })
                        }
                        checked={getChecked(id)}
                      >
                        <p className={styles.storeNameText}>{name}</p>
                      </RadioButton>
                      {bopisQuantity > 0 && (
                        <p className={styles.availabilityText}>
                          {displayAvailableItems(bopisQuantity)}
                        </p>
                      )}
                    </>
                  ) : (
                    <p className={styles.storeNameWithNoRadioText}>{name}</p>
                  )}
                </div>
                <div className={styles.toggleIcon}>
                  <p className={styles.distanceStyle}>
                    {`${distanceToStore} ${unitOfDistance}`}
                  </p>
                  <Icon
                    className={styles.svgIcon}
                    content={caratIcon}
                    title="Expand"
                  />
                </div>
              </AccordionItemTitle>
              <AccordionItemBody
                hideBodyClassName={classNames(
                  styles.accordionItemBody,
                  styles.accordionItemBodyHidden
                )}
              >
                <div className={styles.accordionItemBodyContent}>
                  <div className={styles.addressAndSchedule}>
                    <div>
                      <div>{address1}</div>
                      <div>{address2}</div>
                      <div>
                        {`${[city, state, postalcode, country]
                          .filter((val) => val)
                          .join(", ")}`}
                      </div>
                    </div>
                    <div>
                      {phone && (
                        <div>
                          <a className="inline-link" href={`tel:${phone}`}>
                            {formattedPhone || phone}
                          </a>
                        </div>
                      )}
                      {hours?.length > 0 && (
                        <div>
                          {hours.map((scheduleGroup) => (
                            <div key={scheduleGroup}>{scheduleGroup}</div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                  <a
                    href={`https://www.google.com/maps/dir/?api=1&destination=${latitude},${longitude}`}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-link"
                  >
                    Get Directions
                  </a>
                </div>
              </AccordionItemBody>
            </AccordionItem>
          );
        }
      )}
    </Accordion>
  );
};

export default BopisModalStoreList;
